import {
	COPY_SONAR_OBJECT_BASE,
	COPY_SONAR_OBJECT_BASE_PLURAL,
	ORGANIZATION_TYPE_LABELS
} from '@sonar-software/sonar-ts-constants';
import {
	PardotTypeRoutePart,
	SalesforceTypeRoutePart,
	SfWorkflowActionType,
	SonarObjectType,
	SonarObjectTypeId
} from '@sonar-software/sonar-ts-types';

function isOfCollection<T>(values: T[], value: T | null | undefined): boolean {
	if (!value || !values.length) {
		return false;
	}

	return values.find((collectionValue) => collectionValue === value) !== undefined;
}

export function isBoolean(value: unknown): boolean {
	return value === Boolean(value);
}

export function isUndefined(value: unknown): value is undefined {
	return value === undefined;
}

export function isNull(value: unknown): value is null {
	return value === null;
}

export function isNumber(value: unknown): value is number {
	return typeof value === 'number';
}

export function isNumeric(value: unknown): boolean {
	return !isNaN(Number(value));
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function isFunction(fn?: unknown): fn is Function {
	return typeof fn === 'function';
}

export function isSonarObjectType(value: string | null | undefined): boolean {
	return isOfCollection(Object.values(SonarObjectType), value);
}

const workflowActionToSonarObjectTypeMap: Record<SfWorkflowActionType, SonarObjectType> = {
	[ SfWorkflowActionType.FieldUpdate ]: SonarObjectType.SalesforceFieldUpdate,
	[ SfWorkflowActionType.EmailAlert ]: SonarObjectType.SalesforceWorkflowAlert
};

export function mapWorkflowActionToSonarObjectType(type: SfWorkflowActionType | string): SonarObjectType {
	return workflowActionToSonarObjectTypeMap[ type as SfWorkflowActionType ];
}

export type ConstRecord<T extends Record<string, unknown>> = T[keyof T];

type SonarObjectTypeMapping = {
	apiVersion: 'v2' | 'v3';
	id: SonarObjectTypeId;
	initiativeExclude?: boolean;
	periscopeRoutePart: SalesforceTypeRoutePart | PardotTypeRoutePart | undefined;
	searchExclude?: boolean;
	sourceLabel: ConstRecord<typeof ORGANIZATION_TYPE_LABELS>;
	typeLabel: ConstRecord<typeof COPY_SONAR_OBJECT_BASE>;
	typeLabelPlural: ConstRecord<typeof COPY_SONAR_OBJECT_BASE_PLURAL>;
};

const sonarObjectTypeMap: Record<SonarObjectType, SonarObjectTypeMapping> = {
	[ SonarObjectType.PardotAutomation ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.PardotAutomation,
		periscopeRoutePart: PardotTypeRoutePart.AutomationRule,
		sourceLabel: ORGANIZATION_TYPE_LABELS.PARDOT,
		typeLabel: COPY_SONAR_OBJECT_BASE.AUTOMATION_RULE,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.AUTOMATION_RULE
	},
	[ SonarObjectType.PardotEmail ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.PardotEmail,
		periscopeRoutePart: PardotTypeRoutePart.Email,
		sourceLabel: ORGANIZATION_TYPE_LABELS.PARDOT,
		typeLabel: COPY_SONAR_OBJECT_BASE.EMAIL,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.EMAIL
	},
	[ SonarObjectType.PardotEmailTemplate ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.PardotEmailTemplate,
		periscopeRoutePart: PardotTypeRoutePart.EmailTemplate,
		sourceLabel: ORGANIZATION_TYPE_LABELS.PARDOT,
		typeLabel: COPY_SONAR_OBJECT_BASE.EMAIL_TEMPLATE,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.EMAIL_TEMPLATE
	},
	[ SonarObjectType.PardotEntity ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.PardotEntity,
		periscopeRoutePart: PardotTypeRoutePart.Object,
		sourceLabel: ORGANIZATION_TYPE_LABELS.PARDOT,
		typeLabel: COPY_SONAR_OBJECT_BASE.OBJECT,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.OBJECT
	},
	[ SonarObjectType.PardotField ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.PardotField,
		periscopeRoutePart: PardotTypeRoutePart.Field,
		sourceLabel: ORGANIZATION_TYPE_LABELS.PARDOT,
		typeLabel: COPY_SONAR_OBJECT_BASE.FIELD,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.FIELD
	},
	[ SonarObjectType.PardotForm ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.PardotForm,
		periscopeRoutePart: PardotTypeRoutePart.Form,
		sourceLabel: ORGANIZATION_TYPE_LABELS.PARDOT,
		typeLabel: COPY_SONAR_OBJECT_BASE.FORM,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.FORM
	},
	[ SonarObjectType.PardotLandingPage ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.PardotLandingPage,
		periscopeRoutePart: PardotTypeRoutePart.LandingPage,
		sourceLabel: ORGANIZATION_TYPE_LABELS.PARDOT,
		typeLabel: COPY_SONAR_OBJECT_BASE.LANDING_PAGE,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.LANDING_PAGE
	},
	[ SonarObjectType.PardotList ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.PardotList,
		periscopeRoutePart: PardotTypeRoutePart.List,
		sourceLabel: ORGANIZATION_TYPE_LABELS.PARDOT,
		typeLabel: COPY_SONAR_OBJECT_BASE.LIST,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.LIST
	},
	[ SonarObjectType.PardotPageAction ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.PardotPageAction,
		periscopeRoutePart: PardotTypeRoutePart.PageAction,
		sourceLabel: ORGANIZATION_TYPE_LABELS.PARDOT,
		typeLabel: COPY_SONAR_OBJECT_BASE.PAGE_ACTION,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.PAGE_ACTION
	},
	[ SonarObjectType.PardotProfile ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.PardotProfile,
		periscopeRoutePart: PardotTypeRoutePart.Profile,
		sourceLabel: ORGANIZATION_TYPE_LABELS.PARDOT,
		typeLabel: COPY_SONAR_OBJECT_BASE.PROFILE,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.PROFILE
	},
	[ SonarObjectType.SalesforceApexClass ]: {
		apiVersion: 'v2',
		id: SonarObjectTypeId.SalesforceApexClass,
		periscopeRoutePart: SalesforceTypeRoutePart.ApexClass,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.APEX_CLASS,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.APEX_CLASS
	},
	[ SonarObjectType.SalesforceApexTrigger ]: {
		apiVersion: 'v2',
		id: SonarObjectTypeId.SalesforceApexTrigger,
		periscopeRoutePart: SalesforceTypeRoutePart.ApexTrigger,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.APEX_TRIGGER,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.APEX_TRIGGER
	},
	[ SonarObjectType.SalesforceWorkflowAlert ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.SalesforceEmailAlert,
		periscopeRoutePart: SalesforceTypeRoutePart.EmailAlert,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.EMAIL_ALERT,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.EMAIL_ALERT
	},
	[ SonarObjectType.SalesforceEmailTemplate ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.SalesforceEmailTemplate,
		periscopeRoutePart: SalesforceTypeRoutePart.EmailTemplate,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.EMAIL_TEMPLATE,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.EMAIL_TEMPLATE
	},
	[ SonarObjectType.SalesforceEntity ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.SalesforceEntity,
		periscopeRoutePart: SalesforceTypeRoutePart.Entity,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.OBJECT,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.OBJECT
	},
	[ SonarObjectType.SalesforceField ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.SalesforceField,
		periscopeRoutePart: SalesforceTypeRoutePart.Field,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.FIELD,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.FIELD
	},
	[ SonarObjectType.SalesforceFieldUpdate ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.SalesforceFieldUpdate,
		periscopeRoutePart: SalesforceTypeRoutePart.FieldUpdate,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.FIELD_UPDATE,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.FIELD_UPDATE
	},
	[ SonarObjectType.SalesforceFlow ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.SalesforceFlow,
		periscopeRoutePart: SalesforceTypeRoutePart.Flow,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.FLOW,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.FLOW
	},
	[ SonarObjectType.SalesforcePageLayout ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.SalesforcePageLayout,
		periscopeRoutePart: SalesforceTypeRoutePart.PageLayout,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.PAGE_LAYOUT,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.PAGE_LAYOUT
	},
	[ SonarObjectType.SalesforcePicklistValue ]: {
		apiVersion: 'v2',
		id: SonarObjectTypeId.SalesforcePicklistValue,
		periscopeRoutePart: SalesforceTypeRoutePart.PicklistValue,
		searchExclude: true,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.PICKLIST_VALUE,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.PICKLIST_VALUE
	},
	[ SonarObjectType.SalesforceProcessBuilder ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.SalesforceProcessBuilder,
		periscopeRoutePart: SalesforceTypeRoutePart.ProcessBuilder,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.PROCESS_BUILDER,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.PROCESS_BUILDER
	},
	[ SonarObjectType.SalesforceProfile ]: {
		apiVersion: 'v2',
		id: SonarObjectTypeId.SalesforceProfile,
		initiativeExclude: true,
		periscopeRoutePart: SalesforceTypeRoutePart.Profile,
		searchExclude: true,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.PROFILE,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.PROFILE
	},
	[ SonarObjectType.SalesforceReport ]: {
		apiVersion: 'v2',
		id: SonarObjectTypeId.SalesforceReport,
		periscopeRoutePart: SalesforceTypeRoutePart.Report,
		searchExclude: true,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.REPORT,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.REPORT
	},
	[ SonarObjectType.SalesforceValidationRule ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.SalesforceValidationRule,
		periscopeRoutePart: SalesforceTypeRoutePart.ValidationRule,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.VALIDATION_RULE,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.VALIDATION_RULE
	},
	[ SonarObjectType.SalesforceWorkflowRule ]: {
		apiVersion: 'v3',
		id: SonarObjectTypeId.SalesforceWorkflowRule,
		periscopeRoutePart: SalesforceTypeRoutePart.WorkflowRule,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SALESFORCE,
		typeLabel: COPY_SONAR_OBJECT_BASE.WORKFLOW_RULE,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.WORKFLOW_RULE
	},
	[ SonarObjectType.SonarTag ]: {
		apiVersion: 'v2',
		id: SonarObjectTypeId.SonarTag,
		initiativeExclude: true,
		periscopeRoutePart: undefined,
		sourceLabel: ORGANIZATION_TYPE_LABELS.SONAR,
		typeLabel: COPY_SONAR_OBJECT_BASE.TAG,
		typeLabelPlural: COPY_SONAR_OBJECT_BASE_PLURAL.TAG
	}
};

export function getSonarObjectTypeApiVersion(type: SonarObjectType): string | undefined {
	return sonarObjectTypeMap[ type ]?.apiVersion;
}

export function getSonarObjectTypeId(type: SonarObjectType): SonarObjectTypeId {
	return sonarObjectTypeMap[ type ]?.id as SonarObjectTypeId;
}

export function getSonarObjectTypeFromId(typeId: SonarObjectTypeId): SonarObjectType {
	for (const key in sonarObjectTypeMap) {
		const entry = sonarObjectTypeMap[ key as SonarObjectType ];

		if (entry.id === typeId) {
			return key as SonarObjectType;
		}
	}

	return SonarObjectType.SonarTag;
}

export function getSonarObjectTypeSourceLabel(type: SonarObjectType | null | undefined): string {
	if (!type) {
		return '';
	}

	return sonarObjectTypeMap[ type ]?.sourceLabel as string;
}

export function getSonarObjectTypeLabel(type: SonarObjectType | null | undefined): string | undefined {
	if (!type) {
		return '';
	}

	return sonarObjectTypeMap[ type ]?.typeLabel;
}

export function getSonarObjectTypeLabelPlural(type: SonarObjectType | null | undefined): string | undefined {
	if (!type) {
		return '';
	}

	return sonarObjectTypeMap[ type ]?.typeLabelPlural;
}

export function getSonarObjectTypeAndSourceLabel(type: SonarObjectType): string | undefined {
	return `${getSonarObjectTypeSourceLabel(type)} ${getSonarObjectTypeLabel(type)}`;
}

export function getSonarObjectTypeAndSourceLabelPlural(type: SonarObjectType): string | undefined {
	return `${getSonarObjectTypeSourceLabel(type)} ${getSonarObjectTypeLabelPlural(type)}`;
}

export function getSonarObjectTypePeriscopeRoutePart(type: SonarObjectType): string | undefined {
	return sonarObjectTypeMap[ type ]?.periscopeRoutePart;
}

export function isSonarObjectTypeInitiativeFilterAllowed(type: SonarObjectType): boolean {
	return !sonarObjectTypeMap[ type ]?.initiativeExclude;
}

export function isSonarObjectTypeSearchAllowed(type: SonarObjectType): boolean {
	return !sonarObjectTypeMap[ type ]?.searchExclude;
}

export function isPardotType(type: SonarObjectType | null | undefined): boolean {
	return getSonarObjectTypeSourceLabel(type) === ORGANIZATION_TYPE_LABELS.PARDOT;
}

export function isSonarType(type: SonarObjectType | null | undefined): boolean {
	return getSonarObjectTypeSourceLabel(type) === ORGANIZATION_TYPE_LABELS.SONAR;
}
